import type { EditorView } from "@tiptap/pm/view";
import type { Variables } from "@/lib/sparxs/metadataSchemas";
import {
  DIRECTIVE_CHIP_CHOICE_FIELD_ATTR,
  resolveFloatingChoiceFieldElement,
} from "../../suggestion/storage/session";
import {
  handleDirectiveBuilderListKeyDown,
  type MutableDirectiveBuilderListKeyDownState,
} from "../../suggestion/logic/builderKeydown";

type DirectiveBuilderPluginKeyDownState =
  MutableDirectiveBuilderListKeyDownState & {
    root: unknown;
    container: unknown;
    builder: { step: string };
    floatingUiContainer: HTMLElement | null;
    floatingChoiceFieldEl: HTMLElement | null;
  };

const handleDirectiveBuilderChoiceStepKeyDown = (
  view: EditorView,
  event: KeyboardEvent,
  floatingUiContainer: HTMLElement | null,
  floatingChoiceFieldEl: HTMLElement | null,
  choiceFieldId: string,
): boolean => {
  const t = event.target;
  const inEditorDom = t instanceof Node ? view.dom.contains(t) : false;
  const radixContentOpen = document.querySelector(
    '[role="listbox"][data-state="open"]',
  );
  if (radixContentOpen != null && event.key === "Enter" && inEditorDom) {
    event.preventDefault();
    const focusTarget =
      radixContentOpen.querySelector("[data-highlighted]") ??
      radixContentOpen.querySelector('[role="option"]');
    if (focusTarget instanceof HTMLElement) {
      focusTarget.focus();
    }
    return true;
  }
  if (
    radixContentOpen != null &&
    (event.key === "ArrowDown" || event.key === "ArrowUp")
  ) {
    return false;
  }
  if (!(t instanceof Node) || !view.dom.contains(t)) {
    return false;
  }
  if (
    t instanceof HTMLElement &&
    t.closest(`[${DIRECTIVE_CHIP_CHOICE_FIELD_ATTR}]`) != null
  ) {
    return false;
  }
  if (
    event.key !== "ArrowDown" &&
    event.key !== "ArrowUp" &&
    event.key !== "Enter"
  ) {
    return false;
  }
  const el = resolveFloatingChoiceFieldElement(
    floatingUiContainer,
    floatingChoiceFieldEl,
    choiceFieldId,
  );
  if (!(el instanceof HTMLElement)) {
    return false;
  }
  event.preventDefault();
  el.focus({ preventScroll: true });
  return true;
};

export const handleDirectiveBuilderFloatingKeyDown = (
  view: EditorView | null,
  event: KeyboardEvent,
  stateRef: DirectiveBuilderPluginKeyDownState,
  variables: Variables[],
  choiceFieldId: string,
): boolean => {
  if (stateRef.root == null || stateRef.container == null) {
    return false;
  }
  const { step } = stateRef.builder;
  if (step === "directive" || step === "variable") {
    return handleDirectiveBuilderListKeyDown(stateRef, variables, event);
  }
  if (step === "choice" && view != null) {
    return handleDirectiveBuilderChoiceStepKeyDown(
      view,
      event,
      stateRef.floatingUiContainer,
      stateRef.floatingChoiceFieldEl,
      choiceFieldId,
    );
  }
  return false;
};
